/**
 * Setup-checklist step catalog (kind-alignment §4).
 *
 * One shared step vocabulary; each business kind selects and orders its six.
 * A step is done when the workspace's DATA says so, never because its row was
 * clicked (V38, dogfood V34 FAIL 14):
 *  - `needs`: ANY id in every group is connected (integration registry ids,
 *    plus the Meta-trio/Stripe virtual ids the derivation emits);
 *  - `fact`: a server fact from GET /api/setup/progress holds (a verified
 *    sending domain, a funnel switched on, a named brand, …);
 *  - `visited`: the merchant opened the page the step is about (a flow's
 *    canvas), recorded per user when the page is reached;
 *  - none of these: a "go and look" step, done once taken (inbox, lead board).
 * `needs` and `fact` are alternatives: either one completes the step.
 */
import type { BusinessTypeId } from '../../config/businessPresets';
import type { MessageKey } from '../../i18n';
import { isFeatureLive, type FeatureKey } from '../../config/featureAvailability';

export interface SetupStep {
  id: string;
  titleKey: MessageKey;
  /** Omitted when the step has no body line (the title and CTA say it all). */
  bodyKey?: MessageKey;
  ctaKey: MessageKey;
  route: string;
  /** Any-of groups of integration ids; all groups satisfied ⇒ complete. */
  needs?: string[][];
  /** A server-side fact (GET /api/setup/progress) that completes the step. */
  fact?: SetupFact;
  /** Done when the merchant reaches a path matching this (recorded per user). */
  visited?: RegExp;
  /**
   * The capability the step asks the merchant to set up. While it is not live
   * for a new customer (config/featureAvailability.ts) an incomplete step
   * shows Coming soon instead of a call to action.
   */
  feature?: FeatureKey;
}

/** Mirror of server SETUP_FACTS (services/setup/setupProgressService.ts). */
export type SetupFact =
  | 'email_domain_verified'
  | 'payments_connected'
  | 'funnel_on'
  | 'brand_named'
  | 'catalog_added'
  | 'availability_set'
  | 'content_queued';

const STORE: SetupStep = {
  id: 'store',
  titleKey: 'setup.step.store.title',
  bodyKey: 'setup.step.store.body',
  ctaKey: 'setup.cta.integrations',
  route: '/account?tab=integrations',
  needs: [['shopify', 'woocommerce', 'wix', 'bigcommerce']],
  // Shopify connect is live; Woo/Wix/BigCommerce stay Coming soon.
  feature: 'shopify.connect',
};

const MENU: SetupStep = {
  id: 'menu',
  titleKey: 'setup.step.menu.title',
  bodyKey: 'setup.step.menu.body',
  ctaKey: 'setup.cta.hub',
  route: '/business?tab=inventory',
  fact: 'catalog_added',
};

// Kit FR_STEPS.services `cal`: availability is what bookings, reminders and
// no-show recovery key off. This product's availability is native working
// hours (WorkingHoursPanel), not an external calendar — an empty week is a
// booking page with "no open times", so the step is manual-tick.
const AVAILABILITY: SetupStep = {
  id: 'availability',
  titleKey: 'setup.step.availability.title',
  bodyKey: 'setup.step.availability.body',
  ctaKey: 'setup.cta.calendar',
  route: '/business?tab=calendar',
  fact: 'availability_set',
};

const WHATSAPP: SetupStep = {
  id: 'wa',
  titleKey: 'setup.step.wa.title',
  bodyKey: 'setup.step.wa.body',
  ctaKey: 'setup.cta.integrations',
  route: '/account?tab=integrations',
  needs: [['whatsapp_business']],
  feature: 'whatsapp.messaging',
};

const EMAIL: SetupStep = {
  id: 'email',
  titleKey: 'setup.step.email.title',
  bodyKey: 'setup.step.email.body',
  ctaKey: 'setup.cta.integrations',
  route: '/account?tab=integrations',
  needs: [['email_sending']],
  // A verified sending domain lives on BrandSettings, not an Integration row —
  // the step stayed open on a Verified domain until this.
  fact: 'email_domain_verified',
  feature: 'email.ownDomain',
};

const PAYMENTS: SetupStep = {
  id: 'pay',
  titleKey: 'setup.step.pay.title',
  bodyKey: 'setup.step.pay.body',
  ctaKey: 'setup.cta.integrations',
  route: '/account?tab=integrations',
  needs: [['stripe']],
  fact: 'payments_connected',
};

const ADS: SetupStep = {
  id: 'ads',
  titleKey: 'setup.step.ads.title',
  bodyKey: 'setup.step.ads.body',
  ctaKey: 'setup.cta.integrations',
  route: '/account?tab=integrations',
  needs: [['meta_ads', 'google_ads', 'tiktok_business']],
  feature: 'ads.adAccounts',
};

const SOCIAL_DM: SetupStep = {
  id: 'social_dm',
  titleKey: 'setup.step.socialDm.title',
  bodyKey: 'setup.step.socialDm.body',
  ctaKey: 'setup.cta.integrations',
  route: '/account?tab=integrations',
  needs: [['instagram_dms', 'facebook_messenger']],
  feature: 'instagram.dmAutomation',
};

const FUNNELS: SetupStep = {
  id: 'funnels',
  titleKey: 'setup.step.funnels.title',
  bodyKey: 'setup.step.funnels.body',
  ctaKey: 'setup.cta.funnels',
  route: '/business',
  fact: 'funnel_on',
};

const INBOX: SetupStep = {
  id: 'inbox',
  titleKey: 'setup.step.inbox.title',
  // No body: the old line promised Instagram, Messenger and WhatsApp
  // conversations "answered by AI" — channels that are Coming soon, and AI
  // BuzzBit X does not run. The title and CTA say it all.
  ctaKey: 'setup.cta.inbox',
  route: '/inbox',
};

const LEADS: SetupStep = {
  id: 'leads',
  titleKey: 'setup.step.leads.title',
  bodyKey: 'setup.step.leads.body',
  ctaKey: 'setup.cta.hub',
  route: '/business',
};

const FLOWS: SetupStep = {
  id: 'flows',
  titleKey: 'setup.step.flows.title',
  bodyKey: 'setup.step.flows.body',
  ctaKey: 'setup.cta.automations',
  // The flows live in My Automations; /automations is the canvas lab.
  route: '/studio?tab=my-flows',
  visited: /^\/studio\/flow\/[^/]+$/,
};

const SOCIAL_CAL: SetupStep = {
  id: 'social_cal',
  titleKey: 'setup.step.socialCal.title',
  ctaKey: 'setup.cta.calendar',
  route: '/marketing/social',
  fact: 'content_queued',
};

const BRAND: SetupStep = {
  id: 'brand',
  titleKey: 'setup.step.brand.title',
  bodyKey: 'setup.step.brand.body',
  ctaKey: 'setup.cta.brand',
  route: '/settings/brand',
  fact: 'brand_named',
};

export const SETUP_STEPS: Record<BusinessTypeId, SetupStep[]> = {
  ecommerce: [STORE, EMAIL, ADS, FUNNELS, INBOX, BRAND],
  service: [MENU, AVAILABILITY, WHATSAPP, EMAIL, FUNNELS, LEADS, BRAND],
  coaching: [MENU, AVAILABILITY, WHATSAPP, EMAIL, FUNNELS, LEADS, BRAND],
  course: [EMAIL, PAYMENTS, ADS, FUNNELS, SOCIAL_CAL, BRAND],
  saas: [EMAIL, PAYMENTS, ADS, FUNNELS, FLOWS, BRAND],
  content_creator: [SOCIAL_DM, EMAIL, ADS, FUNNELS, SOCIAL_CAL, BRAND],
};

export interface ResolvedStep extends SetupStep {
  complete: boolean;
  /**
   * True when completion comes only from data — a connection, a fact or a
   * visit — so taking the step records nothing.
   */
  connectionGated: boolean;
  /** Not complete, and what it sets up is not live yet: shown as Coming soon. */
  comingSoon: boolean;
}

export function resolveSteps(
  kind: BusinessTypeId,
  manuallyDone: string[],
  connectedIds: Set<string>,
  facts: ReadonlySet<string> = new Set()
): ResolvedStep[] {
  return SETUP_STEPS[kind].map((step) => {
    const needs = step.needs ?? [];
    const connected = needs.length > 0 && needs.every((group) => group.some((id) => connectedIds.has(id)));
    const factHolds = step.fact !== undefined && facts.has(step.fact);
    const visited = step.visited !== undefined && manuallyDone.includes(step.id);
    const dataGated = needs.length > 0 || step.fact !== undefined || step.visited !== undefined;
    const complete = dataGated ? connected || factHolds || visited : manuallyDone.includes(step.id);
    return {
      ...step,
      connectionGated: dataGated,
      complete,
      comingSoon: !complete && step.feature !== undefined && !isFeatureLive(step.feature),
    };
  });
}
